import { Stack, Typography } from '@mui/material';
import { isFunction } from 'lodash';
import { useEffect } from 'react';
import { Link, Location, Outlet, createBrowserRouter, useLocation, useNavigate } from 'react-router-dom';

import { ChatWidget } from '@/components/chat/ChatWidget';
import { MainLayout } from '@/components/layouts/MainLayout';
import { MapLayout } from '@/components/layouts/MapLayout';
import { PublicMainLayout } from '@/components/layouts/PublicMainLayout';
import { PublicMapLayout } from '@/components/layouts/PublicMapLayout';
import { RootLayout } from '@/components/layouts/RootLayout';
import { SpotterLayout } from '@/components/layouts/SpotterLayout';
import { SpotterSetupLayout } from '@/components/layouts/SpotterSetupLayout';
import { TopSpotLayout } from '@/components/layouts/TopSpotLayout';
import { WavelinkProvider } from '@/components/providers/WavelinkProvider';
import { ROUTE_PREFIX } from '@/config';
import { PublicMapUiContextProvider } from '@/context/MapUiContext';
import { SpotterRouteParamContextProvider } from '@/context/SpotterContext';
import { PublicViewTokenContextProvider } from '@/context/ViewTokenContext';
import { ErrorPage } from '@/pages/ErrorPage';
import { GridSpotterDetails } from '@/pages/GridSpotterDetails';
import { Login } from '@/pages/Login';
import { Map } from '@/pages/Map';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PublicSpotterDetails } from '@/pages/PublicSpotterDetails';
import { SignUp } from '@/pages/SignUp';
import { SofarApi } from '@/pages/SofarApi';
import { SpotterDetails } from '@/pages/SpotterDetails';
import { SpotterRegister } from '@/pages/SpotterRegister';
import { SubscriptionBillingHistory } from '@/pages/SubscriptionBillingHistory';
import { TelemetryManagement } from '@/pages/TelemetryManagement';
import { UiDemo } from '@/pages/UiDemo';
import { UserAccount } from '@/pages/UserAccount';
import { AccountBalancePaymentPage } from '@/pages/payment/AccountBalancePayment';
import { CancelSubscriptionPage } from '@/pages/payment/CancelSubscription';
import { PaymentSettingsPage } from '@/pages/payment/PaymentSettings';
import { SatCreditsAddOnetimePage } from '@/pages/payment/SatCreditsAddOnetime';
import { SatCreditsEditRecurringPage } from '@/pages/payment/SatCreditsEditRecurring';
import { UnusedLinesPage } from '@/pages/payment/UnusedLines';

const Redirect = ({ to }: { to: string | ((location: Location) => string) }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const target = isFunction(to) ? to(location) : to;
    useEffect(() => navigate(target, { replace: true }), [target, navigate]);
    return (
        <Stack sx={{ p: 4, gap: 2 }}>
            <Typography variant="h2">This page has moved!</Typography>
            <Typography>
                You should be redirected automatically in a moment, or you can <Link to={target}>click here</Link>.
            </Typography>
        </Stack>
    );
};

export const dashboardRouter = createBrowserRouter(
    [
        {
            element: <RootLayout />,
            errorElement: <ErrorPage />,
            children: [
                {
                    path: 'login',
                    element: <Login />,
                },
                {
                    path: 'signup',
                    element: <SignUp />,
                },
                {
                    path: 'map',
                    element: <Redirect to="/" />,
                },
                {
                    path: 'register',
                    element: <Redirect to="/setup/register" />,
                },
                {
                    element: (
                        <WavelinkProvider>
                            <Outlet />
                            {/* Mounted here in order to persist between 
                                different outlet routes to main state  */}
                            <ChatWidget />
                        </WavelinkProvider>
                    ),
                    children: [
                        {
                            element: <MapLayout />,
                            children: [
                                {
                                    index: true,
                                    element: <Map mapType="spotter" />,
                                },
                                {
                                    path: 'acoustics',
                                    element: <Map mapType="acoustics" />,
                                },
                            ],
                        },
                        {
                            path: '/',
                            element: <MainLayout />,
                            children: [
                                {
                                    path: 'account',
                                    element: <UserAccount />,
                                },
                                {
                                    path: 'api',
                                    element: <SofarApi />,
                                },
                                {
                                    path: 'billing',
                                    element: <SubscriptionBillingHistory />,
                                },
                                {
                                    path: 'telemetry',
                                    element: <TelemetryManagement />,
                                },
                                {
                                    path: 'payment',
                                    children: [
                                        {
                                            path: '',
                                            element: <PaymentSettingsPage />,
                                        },
                                        {
                                            path: 'satelliteCredits/recurring',
                                            element: <SatCreditsEditRecurringPage />,
                                        },
                                        {
                                            path: 'satelliteCredits/onetime',
                                            element: <SatCreditsAddOnetimePage />,
                                        },
                                        {
                                            path: 'accountBalance',
                                            element: <AccountBalancePaymentPage />,
                                        },
                                        {
                                            path: 'unusedLines',
                                            element: <UnusedLinesPage />,
                                        },
                                        {
                                            path: 'cancel',
                                            element: <CancelSubscriptionPage />,
                                        },
                                    ],
                                },
                                {
                                    path: 'setup',
                                    element: <SpotterSetupLayout />,
                                    children: [
                                        {
                                            path: 'register',
                                            element: <SpotterRegister />,
                                        },
                                    ],
                                },
                                {
                                    path: 'spotter',
                                    element: (
                                        <SpotterRouteParamContextProvider>
                                            <SpotterLayout />
                                        </SpotterRouteParamContextProvider>
                                    ),
                                    children: [
                                        {
                                            path: ':spotterId',
                                            element: <SpotterDetails />,
                                        },
                                        {
                                            path: 'details/:spotterId',
                                            element: (
                                                <Redirect
                                                    to={({ pathname, hash, search }) =>
                                                        `${pathname.replace('details/', '')}${search}${hash}`
                                                    }
                                                />
                                            ),
                                        },
                                    ],
                                },
                                {
                                    path: 'grid',
                                    element: (
                                        <SpotterRouteParamContextProvider viaOceansApi spotterFamily="grid">
                                            <SpotterLayout />
                                        </SpotterRouteParamContextProvider>
                                    ),
                                    children: [
                                        {
                                            path: ':spotterId',
                                            element: <GridSpotterDetails />,
                                        },
                                    ],
                                },
                                {
                                    path: 'uiDemo',
                                    element: <UiDemo />,
                                },
                                {
                                    path: 'telemetry-overview',
                                    element: <Redirect to="/telemetry" />,
                                },
                                {
                                    path: 'sofar-api',
                                    element: <Redirect to="/api" />,
                                },
                                {
                                    path: 'subscription-overview',
                                    element: <Redirect to="/payment" />,
                                },
                                {
                                    path: 'subscriptions/*',
                                    element: <Redirect to="/telemetry" />,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: 'topspot',
                    element: <TopSpotLayout />,
                    errorElement: <ErrorPage />,
                },
                {
                    path: 'public',
                    element: (
                        <PublicViewTokenContextProvider>
                            <PublicMapUiContextProvider>
                                <Outlet />
                            </PublicMapUiContextProvider>
                        </PublicViewTokenContextProvider>
                    ),
                    children: [
                        {
                            path: '',
                            element: <PublicMapLayout />,
                        },
                        {
                            path: '*',
                            element: <PublicMainLayout />,
                            children: [
                                {
                                    path: ':spotterId',
                                    element: (
                                        <SpotterRouteParamContextProvider spotterFamily="shared">
                                            <PublicSpotterDetails />
                                        </SpotterRouteParamContextProvider>
                                    ),
                                },
                            ],
                        },
                    ],
                },
                {
                    path: 'dashboard-v2/*',
                    element: (
                        <Redirect
                            to={({ pathname, hash, search }) =>
                                `${pathname.replace(/dashboard-v2\/?/, '')}${search}${hash}`
                            }
                        />
                    ),
                },
                {
                    path: '*',
                    element: <NotFoundPage />,
                },
            ],
        },
    ],
    {
        basename: ROUTE_PREFIX,
    }
);
